import { BEHIND_PAINT, LINE_WEIGHTS, POCHE_PAINT } from "@pushback-and-chaos/contracts";
import { el } from "./svg-model.js";
/**
 * TIER A — THE GENERATED SHELL (ADR 0009, extended by ADR 0010). Floor
 * slabs with poché hatching, columns, ceiling grid, mullions and glazing,
 * partitions, door frames, stair flight, railings, lift shaft, service
 * void, dimension lines, title block, and ghost-weight exterior context.
 * All arithmetic over the EnvironmentDefinition's shell block: a three-bay
 * clinic and a six-bay office are this same code with different numbers,
 * so they cannot drift apart stylistically. Nothing here is hand-authored
 * geometry.
 *
 * ADR 0010 adds WINGS: runs of bays that carry floors, with open courtyards
 * between them. A core in a courtyard bay stands BEHIND the section plane
 * (drawn in the behind tone, bridged to the wings at every upper floor); a
 * wing shorter than the building carries a roof TERRACE; a BASEMENT is a
 * car park under the ground slab, which becomes the plinth. A definition
 * without wings is the single building the generator always drew.
 *
 * Coordinate system: cm at 1:50, y grows DOWNWARD (SVG). y = 0 is the
 * finished floor line of the ground floor. The building rises into negative
 * y; slabs and ground drop into positive y.
 */
const SLAB = 35; // structural slab + finish, cm
const PARAPET = 40;
const TERRACE_PARAPET = 110;
const COLUMN_W = 30;
const MULLION_SPACING = 150;
const DOOR_W = 90;
const DOOR_H = 210;
const EXTERIOR_MARGIN = 420; // ghost context width each side
const SKY_MARGIN = 130;
const GROUND_DEPTH = 150; // below floor line: foundation + dimension lines
/** Presentation attributes for a line weight — never a CSS class (the <use> trap). */
export function weightAttrs(weight) {
    return { fill: "none", stroke: "currentColor", "stroke-width": String(LINE_WEIGHTS[weight]), "stroke-linecap": "square", "stroke-linejoin": "miter" };
}
function line(x1, y1, x2, y2, weight, extra = {}) {
    return el("line", { x1: f(x1), y1: f(y1), x2: f(x2), y2: f(y2), ...weightAttrs(weight), ...extra });
}
function rect(x, y, w, h, weight, extra = {}) {
    return el("rect", { x: f(x), y: f(y), width: f(w), height: f(h), ...weightAttrs(weight), ...extra });
}
/** A cut slab: solid poché (ADR 0015 step 3 — the boards' black cut), edged at the cut weight. */
function slab(x, yTop, w) {
    return el("rect", { x: f(x), y: f(yTop), width: f(w), height: String(SLAB), fill: POCHE_PAINT, stroke: "currentColor", "stroke-width": String(LINE_WEIGHTS.cut), "data-part": "slab" });
}
function f(n) {
    const r = Math.round(n * 100) / 100;
    return Object.is(r, -0) ? "0" : String(r);
}
export function shellMetrics(shell) {
    const buildingW = shell.bays * shell.bayWidthCm;
    const clearH = shell.floorToFloorCm - SLAB;
    const basementH = shell.basement === null ? 0 : shell.basement.heightCm;
    const floorY = (floor) => (floor < 0 ? SLAB + basementH : -floor * shell.floorToFloorCm);
    const wings = shell.wings === null
        ? [{ id: "building", x0: 0, x1: buildingW, floors: shell.floors }]
        : [...shell.wings]
            .sort((a, b) => a.bays.from - b.bays.from)
            .map((w) => ({ id: w.id, x0: w.bays.from * shell.bayWidthCm, x1: (w.bays.to + 1) * shell.bayWidthCm, floors: Math.min(w.floors, shell.floors) }));
    const courtyards = [];
    let cursor = 0;
    for (const wing of wings) {
        if (wing.x0 > cursor)
            courtyards.push({ x0: cursor, x1: wing.x0 });
        cursor = wing.x1;
    }
    if (cursor < buildingW)
        courtyards.push({ x0: cursor, x1: buildingW });
    const wingAt = (x) => wings.find((w) => x >= w.x0 && x <= w.x1) ?? null;
    // Below the ground floor: foundation and dimension lines, plus the car park when there is one.
    const belowGround = SLAB + GROUND_DEPTH + (shell.basement === null ? 0 : basementH + SLAB);
    let viewBox;
    if (shell.focus !== null) {
        // THE ZOOM: crop to the focus floors, neighbors bleeding in at the
        // edges. Margins are tight — readability is the point of the crop.
        const bleed = shell.focus.bleedCm;
        const marginLeft = shell.exterior.left === "none" ? 60 : 330;
        const marginRight = shell.exterior.right === "none" ? 60 : 250;
        const top = floorY(shell.focus.toFloor + 1) + SLAB - bleed;
        const bottom = floorY(shell.focus.fromFloor) + SLAB + bleed;
        viewBox = { x: -marginLeft, y: top, w: buildingW + marginLeft + marginRight, h: bottom - top };
    }
    else {
        const topY = floorY(shell.floors) - PARAPET - SKY_MARGIN;
        viewBox = {
            x: -EXTERIOR_MARGIN,
            y: topY,
            w: buildingW + 2 * EXTERIOR_MARGIN,
            h: -topY + belowGround,
        };
    }
    return {
        viewBox,
        extents: { top: floorY(shell.floors) - PARAPET - SKY_MARGIN, bottom: belowGround },
        buildingW,
        clearH,
        floorY,
        roomSpan: (room) => ({ x0: room.bays.from * shell.bayWidthCm, x1: (room.bays.to + 1) * shell.bayWidthCm }),
        wings,
        courtyards,
        wingAt,
    };
}
/** Shell-level defs. Poché is a solid token fill since the restyle (no pattern), so there are none; kept as the one place to add them. */
export function pocheDefs() {
    return [];
}
/** The slab thickness, for anything that must sit under or over a slab (the composer's room wash). */
export const SLAB_CM = SLAB;
/** Rooms on a floor whose bay span overlaps a wing. */
function roomsIn(def, m, wing, floor) {
    return def.rooms.filter((room) => {
        if (room.floor !== floor)
            return false;
        const span = m.roomSpan(room);
        return span.x0 < wing.x1 && span.x1 > wing.x0;
    });
}
/**
 * Generates the whole shell. Rooms are needed for partitions, doors and
 * labels; everything else comes from the shell block.
 */
export function generateShell(def) {
    const shell = def.shell;
    const m = shellMetrics(shell);
    const nodes = [];
    const W = m.buildingW;
    const groundInFrame = shell.focus === null || shell.focus.fromFloor === 0;
    // ---- ground line: heaviest cut, full width including exterior (only
    // meaningful when the ground is in frame). Over a car park the ground
    // inside the building is the plinth slab, so only the outside runs. ----
    if (groundInFrame) {
        if (shell.basement === null) {
            nodes.push(line(m.viewBox.x, SLAB, m.viewBox.x + m.viewBox.w, SLAB, "cut"));
        }
        else {
            nodes.push(line(m.viewBox.x, SLAB, 0, SLAB, "cut"), line(W, SLAB, m.viewBox.x + m.viewBox.w, SLAB, "cut"));
        }
    }
    // ---- slabs (cut + poché): the ground slab spans the whole building (the
    // plinth over a courtyard or a car park); every upper slab belongs to a
    // wing and stops at its edges ----
    nodes.push(slab(0, m.floorY(0), W));
    for (let floor = 1; floor <= shell.floors; floor += 1) {
        for (const wing of m.wings) {
            if (wing.floors >= floor)
                nodes.push(slab(wing.x0, m.floorY(floor), wing.x1 - wing.x0));
        }
    }
    // ---- parapets on full-height roofs; a terrace on every shorter wing ----
    for (const wing of m.wings) {
        const roofY = m.floorY(wing.floors);
        if (wing.floors === shell.floors) {
            nodes.push(rect(wing.x0, roofY - PARAPET, 14, PARAPET, "cut", { fill: POCHE_PAINT }));
            nodes.push(rect(wing.x1 - 14, roofY - PARAPET, 14, PARAPET, "cut", { fill: POCHE_PAINT }));
        }
        else {
            const terrace = [];
            // parapet on the outer edge(s), a glass balustrade toward the courtyard
            if (wing.x1 === W)
                terrace.push(rect(wing.x1 - 14, roofY - TERRACE_PARAPET, 14, TERRACE_PARAPET, "cut", { fill: POCHE_PAINT }));
            else
                terrace.push(line(wing.x1 - 6, roofY, wing.x1 - 6, roofY - TERRACE_PARAPET, "obj"), line(wing.x1 - 60, roofY - TERRACE_PARAPET + 6, wing.x1 - 6, roofY - TERRACE_PARAPET + 6, "det"));
            if (wing.x0 === 0)
                terrace.push(rect(0, roofY - TERRACE_PARAPET, 14, TERRACE_PARAPET, "cut", { fill: POCHE_PAINT }));
            else
                terrace.push(line(wing.x0 + 6, roofY, wing.x0 + 6, roofY - TERRACE_PARAPET, "obj"), line(wing.x0 + 6, roofY - TERRACE_PARAPET + 6, wing.x0 + 60, roofY - TERRACE_PARAPET + 6, "det"));
            nodes.push(el("g", { "data-part": `terrace-${wing.id}` }, terrace));
        }
    }
    // ---- the car park: walls, bay-line columns, floor slab ----
    if (shell.basement !== null) {
        const top = SLAB;
        const bottom = m.floorY(-1);
        const cellar = [line(0, top, 0, bottom, "cut"), line(W, top, W, bottom, "cut")];
        for (let bay = 1; bay < shell.bays; bay += 1) {
            cellar.push(rect(bay * shell.bayWidthCm - COLUMN_W / 2, top, COLUMN_W, bottom - top, "obj", { "data-part": "column" }));
        }
        cellar.push(slab(0, bottom, W));
        nodes.push(el("g", { "data-part": "basement" }, cellar));
    }
    // ---- per wing, per storey: service void, columns, glazed faces ----
    for (const wing of m.wings) {
        for (let floor = 0; floor < wing.floors; floor += 1) {
            const yFloor = m.floorY(floor);
            const yCeil = m.floorY(floor + 1) + SLAB; // underside of the slab above
            const rooms = roomsIn(def, m, wing, floor);
            // The void line only — the ceiling-grid tick marks used to hatch this
            // line every 120cm, right through the room labels' letterforms.
            if (shell.serviceVoidCm > 0 && rooms.every((r) => r.serviceVoid)) {
                nodes.push(line(wing.x0, yCeil + shell.serviceVoidCm, wing.x1, yCeil + shell.serviceVoidCm, "det", { "data-part": "service-void" }));
            }
            // columns at bay lines (obj weight; cut where they meet slabs), except
            // inside an open-plan room's span
            for (let x = wing.x0; x <= wing.x1; x += shell.bayWidthCm) {
                const open = rooms.some((r) => r.openPlan && m.roomSpan(r).x0 < x && x < m.roomSpan(r).x1);
                if (open)
                    continue;
                const cx = Math.min(Math.max(x - COLUMN_W / 2, wing.x0), wing.x1 - COLUMN_W);
                nodes.push(rect(cx, yCeil, COLUMN_W, yFloor - yCeil, "obj", { "data-part": "column" }));
            }
            // glazed faces: mullions + glazing lines on both edges of the wing
            for (const [xEdge, inset] of [[wing.x0, 6], [wing.x1, -6]]) {
                nodes.push(line(xEdge, yCeil, xEdge, yFloor, "cut"));
                for (let y = yCeil + MULLION_SPACING; y < yFloor; y += MULLION_SPACING) {
                    nodes.push(line(xEdge + inset - 4, y, xEdge + inset + 4, y, "det"));
                }
            }
        }
    }
    // ---- partitions between adjacent rooms + door frames ----
    for (const p of partitionLines(def, m))
        nodes.push(...p);
    // ---- core: in the plane, or behind a courtyard ----
    if (shell.coreAtBay !== null) {
        const x0 = shell.coreAtBay * shell.bayWidthCm;
        const inCourtyard = m.courtyards.some((c) => x0 >= c.x0 && x0 < c.x1);
        if (inCourtyard) {
            nodes.push(el("g", { "data-part": "core", "data-depth": "behind", color: BEHIND_PAINT }, courtyardCoreNodes(shell, m, x0)));
        }
        else {
            nodes.push(el("g", { "data-part": "core" }, coreNodes(shell, m)));
        }
    }
    // ---- dimension lines below ground (establishing framing only) ----
    const belowY = shell.basement === null ? SLAB : m.floorY(-1) + SLAB;
    if (shell.focus === null) {
        const dimY = belowY + 70;
        const dims = [line(0, dimY, W, dimY, "det")];
        for (let bay = 0; bay <= shell.bays; bay += 1) {
            const x = bay * shell.bayWidthCm;
            dims.push(line(x, dimY - 8, x, dimY + 8, "det"));
            // oblique dimension tick, drafting convention
            dims.push(line(x - 5, dimY + 5, x + 5, dimY - 5, "det"));
        }
        nodes.push(el("g", { "data-part": "dimensions", opacity: "0.7" }, dims));
    }
    // ---- title block: bottom-right of whatever frame we are drawing ----
    const titleY = shell.focus === null ? belowY + 108 : m.viewBox.y + m.viewBox.h - 26;
    const titleX = shell.focus === null ? W : m.viewBox.x + m.viewBox.w - 24;
    nodes.push(el("g", { "data-part": "title-block" }, [
        line(titleX - 340, titleY, titleX, titleY, "det"),
        el("text", { x: f(titleX - 340), y: f(titleY + 24), fill: "currentColor", stroke: "none", "font-size": "22", "letter-spacing": "0.12em", "font-family": "Museo, ui-sans-serif, system-ui, sans-serif" }, [], def.shell.titleBlockLabel.toUpperCase()),
    ]));
    // ---- ghost exterior context ----
    const exterior = [];
    if (shell.exterior.left !== "none")
        exterior.push(...exteriorContext(shell, m, shell.exterior.left, "left"));
    if (shell.exterior.right !== "none")
        exterior.push(...exteriorContext(shell, m, shell.exterior.right, "right"));
    nodes.push(el("g", { "data-part": "exterior", opacity: "0.34" }, exterior));
    return { defs: pocheDefs(), nodes, metrics: m };
}
/**
 * Interior partitions at room boundaries (same floor, ADJACENT bays, the
 * same wing, both in the plane), each with a door frame. A courtyard is not
 * a wall; a room behind the plane has no partition.
 */
function partitionLines(def, m) {
    const out = [];
    const byFloor = new Map();
    for (const room of def.rooms) {
        if (room.depth !== "plane")
            continue;
        const list = byFloor.get(room.floor) ?? [];
        list.push(room);
        byFloor.set(room.floor, list);
    }
    for (const [floor, rooms] of byFloor) {
        const sorted = [...rooms].sort((a, b) => a.bays.from - b.bays.from);
        const yFloor = m.floorY(floor);
        const yCeil = m.floorY(floor + 1) + SLAB;
        for (let i = 0; i + 1 < sorted.length; i += 1) {
            const left = sorted[i];
            const right = sorted[i + 1];
            if (left === undefined || right === undefined)
                continue;
            if (right.bays.from !== left.bays.to + 1)
                continue;
            const x = right.bays.from * def.shell.bayWidthCm;
            const wing = m.wingAt(x);
            if (wing === null || m.wingAt(x - 1) !== wing || m.wingAt(x + 1) !== wing)
                continue;
            const doorTop = yFloor - DOOR_H;
            const partition = [
                // wall above the door opening
                line(x, yCeil, x, doorTop, "obj"),
                // door frame: jambs + open leaf at 30°
                line(x, doorTop, x, doorTop + 6, "obj"),
                line(x - 2, doorTop, x + 2, doorTop, "obj"),
                el("path", {
                    d: `M ${f(x)} ${f(yFloor)} L ${f(x + DOOR_W * 0.5)} ${f(yFloor - DOOR_H * 0.94)}`,
                    ...weightAttrs("det"),
                }),
            ];
            out.push([el("g", { "data-part": `partition-${left.id}` }, partition)]);
        }
    }
    return out;
}
/** The vertical core IN the plane: stair flight with railing, lift shaft with cross on every storey. */
function coreNodes(shell, m) {
    const coreBay = shell.coreAtBay ?? 0;
    const x0 = coreBay * shell.bayWidthCm;
    const w = shell.bayWidthCm;
    // The stair joins the FOCUS floors (our two storeys); without a focus it
    // joins the bottom pair.
    const stairFrom = shell.focus?.fromFloor ?? 0;
    const yFloor = m.floorY(stairFrom);
    const yCeil = m.floorY(Math.min(stairFrom + 1, shell.floors)) + SLAB;
    const nodes = [];
    // lift shaft: left half of the core bay, through every storey
    const shaftW = w * 0.32;
    for (let storey = 0; storey < shell.floors; storey += 1) {
        const sFloor = m.floorY(storey);
        const sCeil = m.floorY(storey + 1) + SLAB;
        nodes.push(rect(x0 + 20, sCeil, shaftW, sFloor - sCeil, "obj"));
        nodes.push(line(x0 + 20, sCeil, x0 + 20 + shaftW, sFloor, "det"));
        nodes.push(line(x0 + 20 + shaftW, sCeil, x0 + 20, sFloor, "det"));
    }
    // stair flight: right half, steps as a zigzag climbing the floor height
    const steps = 13;
    const runX0 = x0 + shaftW + 60;
    const runW = w - shaftW - 100;
    const stepW = runW / steps;
    const stepH = (yFloor - yCeil) / steps;
    let d = `M ${f(runX0)} ${f(yFloor)}`;
    for (let i = 1; i <= steps; i += 1) {
        d += ` L ${f(runX0 + i * stepW)} ${f(yFloor - (i - 1) * stepH)} L ${f(runX0 + i * stepW)} ${f(yFloor - i * stepH)}`;
    }
    nodes.push(el("path", { d, ...weightAttrs("obj") }));
    // railing: parallel to the flight, 90cm above the nosing line
    nodes.push(line(runX0, yFloor - 90, runX0 + runW, yCeil - 90 + stepH, "det"));
    // balusters
    for (let i = 1; i < steps; i += 3) {
        const bx = runX0 + i * stepW;
        const nosingY = yFloor - i * stepH;
        nodes.push(line(bx, nosingY, bx, nosingY - 90, "det"));
    }
    return nodes;
}
/**
 * The core BEHIND a courtyard (ADR 0010): the lift shaft rising to the
 * tallest neighbouring wing, a door at every floor, a stair flight on every
 * storey the bridges serve, a bridge linking the wings at every upper floor
 * both wings reach, and the rear wall's window heads.
 */
function courtyardCoreNodes(shell, m, x0) {
    const w = shell.bayWidthCm;
    const neighbours = m.wings.filter((wing) => wing.x1 === x0 || wing.x0 === x0 + w);
    const tallest = Math.max(...neighbours.map((n) => n.floors), 1);
    const bridged = neighbours.length > 0 ? Math.min(...neighbours.map((n) => n.floors)) : tallest;
    const nodes = [];
    // rear wall of the courtyard: window heads per storey
    for (let s = 0; s <= bridged; s += 1)
        nodes.push(line(x0, m.floorY(s) - 215, x0 + w, m.floorY(s) - 215, "ghost"));
    // lift shaft through every storey, a door at every floor
    const shaftX = x0 + 90;
    const shaftW = 190;
    const coreTop = m.floorY(tallest) - PARAPET;
    nodes.push(rect(shaftX, coreTop, shaftW, SLAB - coreTop, "obj"));
    for (let s = 0; s < tallest; s += 1) {
        const yF = m.floorY(s);
        const yC = m.floorY(s + 1) + SLAB;
        nodes.push(line(shaftX, yC, shaftX + shaftW, yF, "det"), line(shaftX + shaftW, yC, shaftX, yF, "det"));
        nodes.push(rect(shaftX + 50, yF - DOOR_H, DOOR_W, DOOR_H, "obj"));
    }
    // one flight per storey the bridges serve
    const runX0 = x0 + 330;
    const runW = 240;
    const steps = 13;
    for (let s = 0; s < bridged; s += 1) {
        const yF = m.floorY(s);
        const yC = m.floorY(s + 1);
        const stepW = runW / steps;
        const stepH = (yF - yC) / steps;
        let d = `M ${f(runX0)} ${f(yF)}`;
        for (let i = 1; i <= steps; i += 1)
            d += ` L ${f(runX0 + i * stepW)} ${f(yF - (i - 1) * stepH)} L ${f(runX0 + i * stepW)} ${f(yF - i * stepH)}`;
        nodes.push(el("path", { d, ...weightAttrs("obj") }));
        nodes.push(line(runX0, yF - 90, runX0 + runW, yC - 90, "det"));
    }
    // bridges linking the wings at every upper floor: slab + railing
    for (let s = 1; s <= bridged; s += 1) {
        const y = m.floorY(s);
        const bridge = [rect(x0, y, w, 15, "obj"), line(x0, y - 100, x0 + w, y - 100, "det")];
        for (let x = x0 + 40; x < x0 + w; x += 80)
            bridge.push(line(x, y, x, y - 100, "det"));
        nodes.push(el("g", { "data-part": "bridge" }, bridge));
    }
    return nodes;
}
/**
 * Ghost exterior: a street (kerb + lamp), a tree line (real tree symbols are
 * placed by the composer; this draws nothing extra), or a neighboring
 * BUILDING face across a narrow gap — slab stubs and a facade line at every
 * storey, clipped by the crop, which is also where string lights land.
 */
function exteriorContext(shell, m, kind, side) {
    const W = m.buildingW;
    const nodes = [];
    if (kind === "street") {
        const x0 = side === "left" ? m.viewBox.x : W;
        const x1 = side === "left" ? 0 : m.viewBox.x + m.viewBox.w;
        const w = x1 - x0;
        // kerb + a simple lamp; ornate lamps are ingested symbols the composer places
        nodes.push(line(x0 + w * 0.1, SLAB - 6, x1 - w * 0.05, SLAB - 6, "ghost"));
        const lampX = x0 + w * 0.55;
        nodes.push(line(lampX, SLAB, lampX, SLAB - 320, "ghost"));
        nodes.push(line(lampX, SLAB - 320, lampX + (side === "left" ? 46 : -46), SLAB - 320, "ghost"));
    }
    else if (kind === "building") {
        // The neighbor across the alley: facade line + slab stubs per storey.
        const gap = 300;
        const faceX = side === "left" ? -gap : W + gap;
        const stubDir = side === "left" ? -1 : 1;
        const topY = m.floorY(shell.floors) - PARAPET;
        nodes.push(line(faceX, m.viewBox.y + m.viewBox.h, faceX, Math.max(topY, m.viewBox.y), "ghost"));
        for (let storey = 0; storey <= shell.floors; storey += 1) {
            const y = m.floorY(storey);
            nodes.push(line(faceX, y, faceX + stubDir * 140, y, "ghost"));
            nodes.push(line(faceX, y + SLAB, faceX + stubDir * 140, y + SLAB, "ghost"));
            // a hint of the neighbor's window heads
            nodes.push(line(faceX, y - 210, faceX + stubDir * 110, y - 210, "ghost"));
        }
    }
    else {
        // tree line: trunks only as a fallback; real canopies are ingested symbols
        const x0 = side === "left" ? m.viewBox.x : W + 40;
        const x1 = side === "left" ? -40 : m.viewBox.x + m.viewBox.w;
        for (const tx of [x0 + (x1 - x0) * 0.35, x0 + (x1 - x0) * 0.75]) {
            nodes.push(line(tx, SLAB, tx, SLAB - 210, "ghost"));
            nodes.push(el("circle", { cx: f(tx), cy: f(SLAB - 290), r: "90", ...weightAttrs("ghost") }));
        }
    }
    return nodes;
}
//# sourceMappingURL=shell.js.map